import React, { useState } from 'react';
import axios from 'axios';
import { Search, BookOpen, Languages, X, Loader2, Sparkles, ChevronRight, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScriptureSearch({ onInsert, onClose }) {
  const [activeTab, setActiveTab] = useState('finder');
  const [phrase, setPhrase] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(-1);

  // Word study state
  const [word, setWord] = useState('');
  const [wordLanguage, setWordLanguage] = useState('greek');
  const [wordStudy, setWordStudy] = useState(null);

  const findScriptures = async () => {
    if (!phrase.trim()) return;
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const response = await axios.post(`${API}/scripture/find-by-phrase`, { phrase: phrase.trim() });
      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to find scriptures. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const doWordStudy = async () => {
    if (!word.trim()) return;
    setLoading(true);
    setError('');
    setWordStudy(null);
    try {
      const response = await axios.post(`${API}/scripture/word-study`, { word, language: wordLanguage });
      setWordStudy(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Word study failed');
    } finally {
      setLoading(false);
    }
  };

  const copyVerse = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(-1), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="scripture-search-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1B3A6B]">Scripture Tools</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="scripture-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('finder')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'finder' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
              }`}
              data-testid="scripture-tab-finder"
            >
              <Sparkles className="w-4 h-4" />
              Find Scripture
            </button>
            <button
              onClick={() => setActiveTab('wordstudy')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'wordstudy' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
              }`}
              data-testid="scripture-tab-wordstudy"
            >
              <Languages className="w-4 h-4" />
              Word Study
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {activeTab === 'finder' && (
            <div>
              <p className="text-sm text-[#475569] mb-4">Type a phrase, topic, or feeling — we'll find the scriptures that match.</p>
              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && findScriptures()}
                  placeholder="e.g. feeling anxious, God's love for me, strength in weakness"
                  className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
                  data-testid="scripture-phrase-input"
                />
                <button
                  onClick={findScriptures}
                  disabled={loading || !phrase.trim()}
                  className="btn-primary px-5 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                  data-testid="scripture-find-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Find
                </button>
              </div>

              {/* Quick suggestions */}
              <div className="flex flex-wrap gap-2 mb-6">
                {['feeling anxious', 'finding purpose', 'God\'s promises', 'forgiveness', 'strength in trials', 'trusting God', 'hope for the future', 'overcoming fear'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setPhrase(s); }}
                    className="px-3 py-1.5 bg-[#F4F2EC] text-[#475569] rounded-full text-xs hover:bg-[#1B3A6B] hover:text-white transition-colors"
                    data-testid={`suggestion-${s.replace(/\s+/g, '-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1B3A6B] mx-auto mb-3" />
                  <p className="text-sm text-[#475569]">Finding scriptures that speak to "{phrase}"...</p>
                </div>
              )}

              {results && (
                <div className="space-y-4" data-testid="scripture-results">
                  {results.scriptures?.map((s, idx) => (
                    <div key={idx} className="bg-[#FFF8E7] border border-[#B8860B]/20 rounded-xl p-4 sm:p-5" data-testid={`scripture-result-${idx}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-[#1B3A6B] text-sm">{s.reference}</h4>
                          {s.correlation && (
                            <span className="text-xs text-[#B8860B] font-medium">{s.correlation}</span>
                          )}
                        </div>
                        <button
                          onClick={() => copyVerse(`${s.text} — ${s.reference}`, idx)}
                          className="p-1.5 hover:bg-white/60 rounded-lg transition-colors"
                          data-testid={`copy-verse-${idx}`}
                        >
                          {copiedIdx === idx ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                        </button>
                      </div>
                      <blockquote className="text-[#334155] text-sm leading-relaxed italic mb-2">
                        "{s.text}"
                      </blockquote>
                      {s.explanation && (
                        <p className="text-xs text-[#475569] leading-relaxed border-t border-[#B8860B]/10 pt-2 mt-2">
                          {s.explanation}
                        </p>
                      )}
                      {onInsert && (
                        <button
                          onClick={() => onInsert({ reference: s.reference, text: s.text })}
                          className="mt-2 flex items-center gap-1 text-xs text-[#1B3A6B] font-medium hover:underline"
                        >
                          Insert into study <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}

                  {results.theme_summary && (
                    <div className="p-4 bg-[#F4F2EC] rounded-xl">
                      <h4 className="text-sm font-semibold text-[#1B3A6B] mb-1">Theme Summary</h4>
                      <p className="text-sm text-[#475569] leading-relaxed">{results.theme_summary}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'wordstudy' && (
            <div>
              <p className="text-sm text-[#475569] mb-4">Study the original Greek or Hebrew meaning of biblical words.</p>
              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doWordStudy()}
                  placeholder="Enter a word (e.g., agape, shalom, logos)"
                  className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
                  data-testid="word-study-input"
                />
                <select
                  value={wordLanguage}
                  onChange={(e) => setWordLanguage(e.target.value)}
                  className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
                  data-testid="word-language-select"
                >
                  <option value="greek">Greek (NT)</option>
                  <option value="hebrew">Hebrew (OT)</option>
                </select>
                <button
                  onClick={doWordStudy}
                  disabled={loading}
                  className="btn-primary px-5 rounded-lg flex items-center gap-2 text-sm"
                  data-testid="word-study-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Study
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">{error}</div>
              )}

              {loading && activeTab === 'wordstudy' && (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1B3A6B] mx-auto mb-3" />
                  <p className="text-sm text-[#475569]">Researching word origins and biblical usage...</p>
                </div>
              )}

              {wordStudy && (
                <div className="bg-white border border-[#E2DFD8] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#1B3A6B] text-white rounded-full text-xs font-medium">
                      {wordStudy.language?.toUpperCase()}
                    </span>
                    <h3 className="text-lg font-bold text-[#1B3A6B]">{wordStudy.word}</h3>
                  </div>
                  <div 
                    className="preview-content text-sm"
                    dangerouslySetInnerHTML={{ 
                      __html: wordStudy.study
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>')
                    }} 
                  />
                </div>
              )}

              <div className="mt-6 p-4 bg-[#F4F2EC] rounded-lg">
                <h4 className="font-medium text-[#0F1A2A] mb-2 text-sm">Popular Words:</h4>
                <div className="flex flex-wrap gap-2">
                  {['agape', 'logos', 'pneuma', 'charis', 'pistis', 'shalom', 'hesed', 'emet', 'ruach', 'nephesh'].map(w => (
                    <button
                      key={w}
                      onClick={() => { setWord(w); setWordLanguage(['shalom','hesed','emet','ruach','nephesh'].includes(w) ? 'hebrew' : 'greek'); }}
                      className="px-3 py-1.5 bg-white border border-[#E2DFD8] rounded-full text-xs text-[#475569] hover:bg-[#1B3A6B] hover:text-white hover:border-[#1B3A6B] transition-colors"
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
